// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
} from 'react';
import { apiClient } from '../services/api-client.js';

/** Proactive refresh interval: refresh session every 25 minutes (well before 30-min expiry). */
const REFRESH_INTERVAL_MS = 25 * 60 * 1000;

interface UserInfo {
  user_id: string;
  email: string;
  name: string;
  role_name: string;
  role_id: string;
  org_id: string;
  permissions: string[];
  mfa_required?: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  loading: boolean;
  user: UserInfo | null;
}

interface AuthContextValue extends AuthState {
  login: (user: any) => void;
  logout: () => void;
}

// Exported so consumers that need a non-throwing read of auth state
// (e.g. components rendered in tests without an AuthProvider) can use
// `useContext(AuthContext)` directly and get `null` instead of an
// exception.
//
// fix-sprint-20260411-0417 Finding 15 — FormSectionRenderer reads the
// active org id to key the semantic-options cache; the throwing
// `useAuth` is unsuitable for that case because the component is
// instantiated by tests that mock api-client without mounting an
// AuthProvider.
export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Auth provider that manages session state via httpOnly cookies.
 * Tokens are managed by the backend (set/cleared via Set-Cookie headers).
 * Frontend only tracks authentication state and user info.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    loading: true,
    user: null,
  });

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Proactive session refresh: call /auth/refresh periodically to keep the
  // session alive while the user is on the page. The backend sliding window
  // handles active API usage, but this covers idle-but-present scenarios
  // (e.g. reading a long report without clicking).
  const startRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(async () => {
      try {
        // The refresh cookie is httpOnly and sent automatically.
        // Passing empty object — backend reads token from cookie first.
        await apiClient.refreshToken({} as any);
      } catch {
        // Refresh failed — session will expire naturally and 401 handler kicks in
      }
    }, REFRESH_INTERVAL_MS);
  }, []);

  const stopRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  // Wire auto-logout on 401 responses (F-H5)
  useEffect(() => {
    apiClient.setOnUnauthorized(() => {
      stopRefreshTimer();
      setState({
        isAuthenticated: false,
        loading: false,
        user: null,
      });
    });
  }, [stopRefreshTimer]);

  // On mount, validate session by calling getMe() — cookie is sent automatically
  useEffect(() => {
    apiClient
      .getMe()
      .then((profile: any) => {
        const user = profile.user;
        const role = profile.role;

        setState({
          isAuthenticated: true,
          loading: false,
          user: {
            user_id: user.user_id || user._id,
            email: user.email,
            name: user.name,
            role_name: role.name || '',
            role_id: role.role_id || '',
            org_id: user.org_id || '',
            permissions: role.permissions || [],
          },
        });
        startRefreshTimer();
      })
      .catch(() => {
        // No valid session cookie — user is not authenticated
        setState({
          isAuthenticated: false,
          loading: false,
          user: null,
        });
      });

    return () => stopRefreshTimer();
  }, [startRefreshTimer, stopRefreshTimer]);

  const login = (user: any) => {
    // Cookie was set by the backend login response — just update UI state
    const userInfo: UserInfo = {
      user_id: user.user_id || user._id,
      email: user.email || '',
      name: user.name || '',
      role_name: user.role_name || '',
      role_id: user.role_id || '',
      org_id: user.org_id || '',
      permissions: user.permissions || [],
      mfa_required: user.mfa_required || false,
    };
    setState({
      isAuthenticated: true,
      loading: false,
      user: userInfo,
    });
    startRefreshTimer();
  };

  const logout = () => {
    stopRefreshTimer();
    // Call backend logout which clears httpOnly cookies
    apiClient.logoutApi().catch(() => {});
    setState({
      isAuthenticated: false,
      loading: false,
      user: null,
    });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
